import { gsap } from 'gsap';
import Draggable from 'gsap/Draggable';
import InertiaPlugin from 'gsap/InertiaPlugin';
import { onWidthResize } from '../utils/resize.js';
gsap.registerPlugin(Draggable, InertiaPlugin);

// px/sec. Half the old banner-matched drift so the row reads as a slow idle
// on phone/tablet too, not just desktop.
const NARROW_SPEED = 35;
const DESKTOP_SPEED = 35;
// Match Instafeed's own mobile check (`max-width: 1024px`).
const NARROW_MQ = '(max-width: 1024px)';
const MIN_POSTS_BEFORE_MARQUEE = 8;
const DRAG_CLICK_THRESHOLD = 6;

export default class InstagramFeed {
    constructor(element) {
        this.element = element;
        this.wrapper = null;
        this.items = [];
        this.tween = null;
        this.draggable = null;
        this.clones = [];
        this.setWidth = 0;
        this.initialised = false;
        this.askedForMorePosts = false;
        this.mobileWaitStarted = 0;
        this.guardAbort = null;

        this.start();
    }

    start = () => {
        // Try immediately in case the app already rendered.
        if (this.tryInit()) return;

        // The Instagram app renders its grid asynchronously, so watch for it and
        // wait until it stops inserting nodes before measuring (debounced).
        this.observer = new MutationObserver(() => {
            clearTimeout(this.settleTimer);
            this.settleTimer = setTimeout(() => {
                if (this.tryInit()) this.observer.disconnect();
            }, 600);
        });
        this.observer.observe(this.element, { childList: true, subtree: true });
    };

    tryInit = () => {
        if (this.initialised) return true;

        const wrapper = this.element.querySelector('.instafeed-new-layout-wrapper');
        if (!wrapper) return false;

        const originals = [...wrapper.querySelectorAll('.instafeed-new-layout-item')].filter(
            (el) => !el.dataset.marqueeClone
        );
        if (!originals.length) return false;

        // Instafeed's columnsMobile is 0, so picturesNeeded is 0 and the app
        // stops after the first post on viewports ≤1024. Ask it to load the
        // desktop count before we clone anything into a marquee.
        if (originals.length < MIN_POSTS_BEFORE_MARQUEE) {
            this.askInstafeedForMorePosts();
            if (!this.mobileWaitStarted) this.mobileWaitStarted = Date.now();
            if (Date.now() - this.mobileWaitStarted < 5000) return false;
        }

        this.wrapper = wrapper;
        this.items = gsap.utils.toArray(originals);
        this.initResponsive();
        this.initialised = true;
        return true;
    };

    askInstafeedForMorePosts = () => {
        if (this.askedForMorePosts) return;
        const instances = window.InstafeedAPI?.settings?.instances;
        if (!instances) return;
        const inst = Object.values(instances)[0];
        if (!inst?.run || !inst.options) return;
        const desktopColumns = parseInt(inst.options.columns, 10) || 10;
        if (parseInt(inst.options.columnsMobile, 10) >= desktopColumns) return;
        this.askedForMorePosts = true;
        inst.options.columnsMobile = String(desktopColumns);
        inst.run();
    };

    // Draggable on every viewport. Autoplay uses the same gsap.ticker as
    // window.lenis — when the first copy has left, x wraps so the cloned
    // copy sits where the originals started (restart, never an empty row).
    initResponsive = () => {
        this.mq = window.matchMedia(NARROW_MQ);
        this.onModeChange = () => this.applyMode();
        this.applyMode();
        this.mq.addEventListener('change', this.onModeChange);
        onWidthResize(this.onResize);
    };

    applyMode = () => {
        this.teardownMode();

        gsap.set(this.wrapper, { clearProps: 'transform,x' });
        if (this.items.length < 2) return;

        this.items.forEach((el) => {
            el.style.removeProperty('width');
            el.style.removeProperty('transform');
        });
        gsap.set(this.items, { clearProps: 'transform,x,xPercent' });

        this.fillOneCopy();
        this.setWidth = this.measureSetWidth();
        if (this.setWidth <= 0) return;

        this.startAutoplay();
        this.initDrag();
        this.bindDragClickGuard();
    };

    fillOneCopy = () => {
        this.clones = [];
        this.items.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.dataset.marqueeClone = 'true';
            this.wrapper.appendChild(clone);
            this.clones.push(clone);
        });
    };

    measureSetWidth = () => {
        const first = this.items[0];
        const firstClone = this.clones[0];
        if (!first || !firstClone) return 0;
        return firstClone.offsetLeft - first.offsetLeft;
    };

    wrapX = (x) => {
        if (!this.setWidth) return 0;
        return gsap.utils.wrap(-this.setWidth, 0, x);
    };

    startAutoplay = () => {
        this.tween?.kill();
        if (!this.setWidth) return;
        const speed = this.mq.matches ? NARROW_SPEED : DESKTOP_SPEED;
        gsap.set(this.wrapper, { x: this.wrapX(gsap.getProperty(this.wrapper, 'x')) });
        this.tween = gsap.to(this.wrapper, {
            x: `-=${this.setWidth}`,
            duration: this.setWidth / speed,
            ease: 'none',
            repeat: -1,
            modifiers: {
                x: (x) => `${this.wrapX(parseFloat(x))}px`,
            },
        });
    };

    initDrag = () => {
        this.draggable = Draggable.create(this.wrapper, {
            type: 'x',
            inertia: true,
            dragClickables: true,
            minimumMovement: DRAG_CLICK_THRESHOLD,
            allowNativeTouchScrolling: true,
            cursor: 'grab',
            activeCursor: 'grabbing',
            zIndexBoost: false,
            liveSnap: {
                x: (value) => this.wrapX(value),
            },
            snap: {
                x: (value) => this.wrapX(value),
            },
            onPressInit: () => {
                this.tween?.pause();
            },
            onRelease: () => {
                if (!this.draggable?.isThrowing) this.startAutoplay();
            },
            onThrowComplete: () => {
                this.startAutoplay();
            },
        })[0];
    };

    onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            if (this.draggable?.isDragging || this.draggable?.isThrowing) return;
            this.setWidth = this.measureSetWidth();
            this.startAutoplay();
        }, 150);
    };

    // Instafeed opens a popup on click; GSAP does not swallow that after a drag.
    bindDragClickGuard = () => {
        this.guardAbort?.abort();
        this.guardAbort = new AbortController();
        const { signal } = this.guardAbort;
        let startX = 0;
        let startY = 0;
        let pressed = false;
        let dragged = false;
        let clearTimer = 0;

        const setDragging = (value) => {
            dragged = value;
            this.wrapper.classList.toggle('is-dragging', value);
        };

        this.wrapper.addEventListener(
            'pointerdown',
            (e) => {
                clearTimeout(clearTimer);
                pressed = true;
                startX = e.clientX;
                startY = e.clientY;
                setDragging(false);
            },
            { signal }
        );

        const onMove = (e) => {
            if (!pressed || dragged) return;
            if (
                Math.abs(e.clientX - startX) > DRAG_CLICK_THRESHOLD ||
                Math.abs(e.clientY - startY) > DRAG_CLICK_THRESHOLD
            ) {
                setDragging(true);
            }
        };

        const endPress = () => {
            if (!pressed) return;
            pressed = false;
            if (!dragged) return;
            clearTimer = window.setTimeout(() => setDragging(false), 80);
        };

        window.addEventListener('pointermove', onMove, { capture: true, signal });
        window.addEventListener('pointerup', endPress, { capture: true, signal });
        window.addEventListener('pointercancel', endPress, { capture: true, signal });

        const blockOpen = (e) => {
            if (!dragged) return;
            if (!this.wrapper.contains(e.target)) return;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        };

        this.element.addEventListener('click', blockOpen, { capture: true, signal });
    };

    teardownMode = () => {
        this.tween?.kill();
        this.tween = null;
        this.draggable?.kill();
        this.draggable = null;
        this.guardAbort?.abort();
        this.guardAbort = null;
        this.wrapper?.classList.remove('is-dragging');
        if (this.clones.length) {
            this.clones.forEach((clone) => clone.remove());
            this.clones = [];
        }
        this.setWidth = 0;
    };
}
